import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import {
  CODE,
  ESTIMATED,
  NAME,
  UNIQUE_IDENTIFIER,
} from 'constants/formFields';
import { ESTIMATED_VALUE_OPTIONS } from 'constants/general';

import GeneralFormFields from './components/GeneralFormFields/GeneralFormFields';

import type IFieldError from 'store/types/IFieldError';
import type { IGeneralPanel } from './interfaces/IGeneralPanel';

import styles from './scss/GeneralPanel.module.scss';

function GeneralPanel({
  control,
  enumData,
  dateOpened,
  onDateOpenedChange,
  recordId = '',
  isUniqueIdentifierEditable = false,
  placeholder,
  vcOptions,
  handleGetVcOptions,
  vcOptionsList,
  listPosition,
  setValue,
}: IGeneralPanel) {
  const { t } = useTranslation();

  return (
    <DataSetsPanel title={t('suppliers.selections.general')} className={styles.fullWidth}>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={NAME}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <TextField
              name={name}
              value={value}
              label={t('suppliers.fields.supplierName')}
              error={error?.message}
              onChange={(event) => onChange(event.target.value)}
              required
              placeholder={placeholder || t('suppliers.fields.supplierName')}
            />
          )}
        />
        <Controller
          control={control}
          name={UNIQUE_IDENTIFIER}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <TextField
              name={name}
              value={value}
              label={t('suppliers.fields.uniqueIdentifier')}
              error={error?.message}
              onChange={(event) => onChange(event.target.value)}
              required
              placeholder={placeholder || t('suppliers.fields.uniqueIdentifier')}
              disabled={!!recordId && !isUniqueIdentifierEditable}
            />
          )}
        />
      </div>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={CODE}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <TextField
              name={name}
              value={value}
              label={t('suppliers.fields.supplierCode')}
              error={error?.message}
              onChange={(event) => onChange(event.target.value)}
              required
              placeholder={placeholder || t('suppliers.fields.supplierCode')}
            />
          )}
        />
        <Controller
          control={control}
          name={ESTIMATED}
          render={({
            field: {
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <Select
              label={t('suppliers.fields.estimated')}
              placeholder={t('suppliers.fields.estimated')}
              options={ESTIMATED_VALUE_OPTIONS}
              value={value?.value ? value : undefined}
              onChange={onChange}
              error={(error as IFieldError)?.value?.message}
              menuPosition="absolute"
              required
            />
          )}
        />
      </div>
      <GeneralFormFields
        control={control}
        enumData={enumData}
        dateOpened={dateOpened}
        onDateOpenedChange={onDateOpenedChange}
        recordId={recordId}
        placeholder={placeholder}
        vcOptions={vcOptions}
        vcOptionsList={vcOptionsList}
        handleGetVcOptions={handleGetVcOptions}
        listPosition={listPosition}
        setValue={setValue}
      />
    </DataSetsPanel>
  );
}

export default GeneralPanel;
